import { parseISO } from 'date-fns';

import { cloneDeep } from 'lodash-es';
import { Coordinate } from '../../../core/common/entities/position/coordinate.class';
import { ApiRecordType } from '../../../core/record-types/entities/api-record-type.entity';
import { Comparable, Trackable } from '../../../helpers/public_api';
import { GridItemDef } from '../../../overviews/grid-view/entities/grid-item.entity';
import { ApiAction } from '../../common/entities/actions/api-action.entity';
import { OrganisationId } from '../../organisations/entities/organisation.entity';
import { get, set } from '../helpers/deep-get-set.helper';
import {
    BrowseStatus,
    Family,
    FragmentId,
    MediaObjectId,
    MediaType,
    MergeStrategy,
    MH2MediaType,
    MH2Types,
    RecordId,
    RecordPhase,
    RecordStatus,
    RecordType,
    RecordTypeStructure,
    Rejection
} from './metadata/metadata.entity';
import { PartialMetadata } from './metadata/partial-metadata.entity';

export class PartialRecord implements Comparable, Trackable, GridItemDef {
    public readonly metadata: PartialMetadata;

    constructor(
        metadata: PartialMetadata = {},
        useDefaultStrategies: boolean = true,
        stripMetadata: boolean = false,
        clone: boolean = false
    ) {
        const _metadata: PartialMetadata = stripMetadata ? this.stripMetadata(metadata) : metadata;
        this.metadata = clone ? cloneDeep(_metadata) : metadata;

        if (useDefaultStrategies) {
            this.metadata.MergeStrategies = {
                Mh2Reuse: MergeStrategy.OVERWRITE,
                Keywords: MergeStrategy.OVERWRITE,
                Permissions: MergeStrategy.OVERWRITE,
                Authors: MergeStrategy.OVERWRITE,
                Address: MergeStrategy.OVERWRITE,
                Relations: MergeStrategy.MERGE,
                PreferredLabels: MergeStrategy.OVERWRITE,
                AlternativeLabels: MergeStrategy.OVERWRITE
            };
        }
    }

    /**
     * MED-3404: Strips all metadata that starts with an underscore.
     * Ex: _links
     * @param metadata
     * @returns
     */
    private stripMetadata(metadata: PartialMetadata): PartialMetadata {
        const strippedMetadata: PartialMetadata = {};

        Object.keys(metadata).forEach(key => {
            if (key.startsWith('_')) {
                return;
            }
            strippedMetadata[key] = metadata[key];
        });

        return strippedMetadata;
    }

    addMergeStrategy(key: string, strategy: MergeStrategy): void {
        if (!this.metadata.MergeStrategies) {
            this.metadata.MergeStrategies = {};
        }
        this.metadata.MergeStrategies[key] = strategy;
    }

    // #region Simplified getters
    getTitle(): string {
        return this.get(Family.Descriptive, 'Title') as string;
    }

    getFragmentId(): FragmentId {
        return this.get(Family.Internal, 'FragmentId') as FragmentId;
    }

    getRecordId(): RecordId {
        return this.get(Family.Internal, 'RecordId') as RecordId;
    }

    getParentRecordId(): RecordId | null {
        return this.get(Family.Structural, 'ParentRecordId') ?? null;
    }

    getOrganisationId(): OrganisationId {
        return this.get(Family.Internal, 'OrganisationId') as OrganisationId;
    }

    getThumbnailUrl(): string {
        return this.get(Family.Internal, 'PathToKeyframeThumb') as string;
    }

    getProfileIds(): string[] {
        return this.get(Family.Context, 'Profiles') || []; // Profiles can be null instead of empty array, this makes it consistent
    }

    getArchiveDate(): Date | null {
        const archiveDate: string | null | undefined = this.get(Family.Administrative, 'ArchiveDate');
        if (!archiveDate) {
            return null;
        }
        return parseISO(archiveDate);
    }

    getType(): MH2MediaType {
        if (this.isNewCollection()) {
            return MH2Types.Mh2Collection;
        } else {
            return this.getMediaHavenType() ?? MediaType.metadataonly;
        }
    }

    getRecordType(): RecordType {
        return this.get(Family.Administrative, 'RecordType') ?? RecordType.Record;
    }

    getMainRecordType(): RecordType {
        return this.get(Family.Administrative, 'MainRecordType') ?? RecordType.Record;
    }

    getRecordStatus(): RecordStatus {
        return this.get(Family.Administrative, 'RecordStatus') as RecordStatus;
    }

    getRecordPhase(): RecordPhase {
        return this.get(Family.RightsManagement, 'RecordPhase') as RecordPhase;
    }

    getRejections(): Rejection[] {
        return this.get2(Family.Administrative, 'RecordRejections', 'Rejection') ?? [];
    }

    getRecordStructure(): RecordTypeStructure {
        return this.get(Family.Structural, 'RecordStructure') as RecordTypeStructure;
    }

    getMediaHavenType(): MediaType {
        return this.get(Family.Administrative, 'Type') as MediaType;
    }

    getApiActions(): ApiAction[] {
        return (this.metadata._links as ApiAction[] | undefined) ?? [];
    }

    getLocation(): Coordinate | null {
        const position: Record<string, any> | null | undefined = this.get(Family.Descriptive, 'Position');

        if (!position) {
            return null;
        }

        const location: string | undefined = position.Location;

        return location ? Coordinate.coordinateFromString(location) : null;
    }

    getFormattedAddress(): string | null {
        return this.get(Family.Descriptive, 'Position')?.Address;
    }

    getClassificationId(): MediaObjectId | null {
        return this.get(Family.Structural, 'ReferenceCodes')?.Classification?.[0] || null;
    }

    getCollectionReferenceCode(): MediaObjectId | null {
        return this.get(Family.Structural, 'ReferenceCodes')?.Mh2Collection?.[0] || null;
    }

    // #endregion

    // #region Simplified assertions

    isConcept(): boolean {
        return this.getRecordPhase() === RecordPhase.Concept;
    }

    isNewCollection(): boolean {
        return this.get(Family.Administrative, 'RecordType') === RecordType.Mh2Collection;
    }

    isThesaurusSuggestion(): boolean {
        return this.getRecordType() === RecordType.ThesaurusSuggestion;
    }

    isUserSuggestion(): boolean {
        return this.getRecordType() === RecordType.UserSuggestion;
    }

    isIntellectualObject(): boolean {
        return this.getRecordStructure() === RecordTypeStructure.Intellectual;
    }

    isPureFragment(): boolean {
        return !!this.get(Family.Internal, 'IsFragment');
    }

    hasFragments(): boolean {
        return !!this.get(Family.Structural, 'Fragments')?.Fragment.length;
    }

    hasNonRepresentationChildren(): boolean {
        return this.get(Family.Structural, 'HasNonRepresentationChildren') ?? false;
    }

    isInCollection(): boolean {
        return this.getCollectionReferenceCode() !== null;
    }

    isProcessing(): boolean {
        return this.getRecordStatus() === RecordStatus.Processing;
    }

    isBrowseProcessing(): boolean {
        const browseStatus: string = this.get(Family.Internal, 'BrowseStatus') as string;
        return browseStatus === BrowseStatus.in_progress;
    }

    doesNotHaveBrowse(): boolean {
        return (
            this.getRecordStatus() === RecordStatus.Rejected ||
            (this.get(Family.Internal, 'BrowseStatus') as BrowseStatus) === BrowseStatus.no_browse
        );
    }

    hasIncompleteBrowse(): boolean {
        const isRejected: boolean = this.getRecordStatus() === RecordStatus.Rejected;
        const invalidBrowseStatuses: string[] = [BrowseStatus.cancelled, BrowseStatus.failed];

        return !isRejected && invalidBrowseStatuses.includes(this.get(Family.Internal, 'BrowseStatus') as BrowseStatus);
    }

    hasPreview(): boolean {
        return this.get(Family.Internal, 'BrowseStatus') === BrowseStatus.completed;
    }

    isFailed(): boolean {
        return this.getRecordStatus() === RecordStatus.Rejected;
    }

    hasFailedJobs(): boolean {
        return this.get(Family.Administrative, 'HasFailedJobs') ?? false;
    }

    hasErrors(): boolean {
        return this.hasSevereErrors();
    }

    hasSevereErrors(): boolean {
        return this.hasFailedJobs() || this.isFailed();
    }

    hasEditRights(): boolean {
        const editRights: boolean = this.get(Family.Context, 'IsEditable') ?? false;
        return editRights;
    }

    canHaveChildren(whiteList?: RecordType[]): boolean {
        let allowedChildren: ApiRecordType[] = this.get(Family.Context, 'AllowedChildren') ?? [];

        if (whiteList) {
            allowedChildren = allowedChildren.filter(child => whiteList.includes(child.Name));
        }
        return allowedChildren.length > 0;
    }
    // #endregion

    // #region Functional getters

    getDefaultUrl(): string {
        if ((this.isIntellectualObject() && this.canHaveChildren()) || this.hasNonRepresentationChildren()) {
            return this.getContentUrl();
        }

        return this.getDetailUrl();
    }

    getDetailUrl(): string {
        const fragmentId: string = this.getFragmentId();

        if (this.isThesaurusSuggestion()) {
            return `/tasks/thesaurus-suggestion/${fragmentId}`;
        }
        if (this.isUserSuggestion()) {
            return `/tasks/user-suggestion/${fragmentId}`;
        }
        if (this.isConcept()) {
            return `/concepts/${fragmentId}`;
        } else {
            return this.getRecordPhase() === RecordPhase.Published
                ? `/published/${fragmentId}`
                : `/archived/${fragmentId}`;
        }
    }

    getShareDefaultUrl(shareId: string): string {
        if ((this.isIntellectualObject() && this.canHaveChildren()) || this.hasNonRepresentationChildren()) {
            return this.getShareContentUrl(shareId);
        }

        return this.getShareDetailUrl(shareId);
    }

    getShareDetailUrl(shareId: string): string {
        return `/shared/${shareId}/single/${this.getFragmentId()}`;
    }

    getShareContentUrl(shareId: string): string {
        return this.getShareDetailUrl(shareId) + '/items';
    }

    getShareEditUrl(shareId: string): string {
        return this.getShareDetailUrl(shareId) + '/edit';
    }

    getEditUrl(): string {
        return this.getDetailUrl() + '/edit';
    }

    getContentUrl(): string {
        return this.getDetailUrl() + '/items';
    }

    getAccessPreviewUrl(): string {
        if (this.hasPreview() && this.getRecordStatus() !== RecordStatus.Destructed) {
            return this.get(Family.Internal, 'PathToPreview') ?? '';
        }

        // Can be removed once new no-preview-thumb is implemented in backend
        return 'assets/images/placeholders/no-preview-thumb.svg';
    }

    // Separate methods for Image Preview and Access preview since these can be different for e.g. pdfs
    getImagePreviewUrl(): string {
        if (this.hasPreview() && this.getRecordStatus() !== RecordStatus.Destructed) {
            return this.get(Family.Internal, 'PathToKeyframe') ?? '';
        }

        // Can be removed once new no-preview-thumb is implemented in backend
        return 'assets/images/placeholders/no-preview-thumb.svg';
    }

    hasOriginal(): boolean {
        return !!this.get(Family.Structural, 'OriginalRecordId');
    }

    getTypeIconName(): string {
        const type: MH2MediaType = this.getType();

        switch (type) {
            case MediaType.image:
            case MediaType.layer:
                return 'image';
            case MediaType.video:
            case MediaType.videofragment:
                return 'video';
            case MediaType.audio:
            case MediaType.audiofragment:
                return 'music';
            case MediaType.document:
            case MediaType.page:
                return 'file';
            case MediaType.newspaper:
            case MediaType.newspaperpage:
            case MediaType.article:
                return 'newspaper';
            case MediaType.filearchive:
                return 'file-zip';
            case MediaType.metadataonly:
                return 'file';
            case MediaType.set:
            case MediaType.collection:
                return 'collection';
            case MediaType.processing:
                return 'hourglass-half';
            case MH2Types.Mh2Collection:
                return 'image-stack';
            default:
                return 'image';
        }
    }

    // #endregion

    // #region Deep Metadata getters and setters
    public get<F extends keyof PartialMetadata, P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>>(
        family: F,
        property: P
    ): NonNullable<NonNullable<PartialMetadata>[F]>[P] {
        return get(this.metadata, family, property);
    }

    public get2<
        F extends keyof NonNullable<PartialMetadata>,
        P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>,
        Q extends keyof NonNullable<NonNullable<NonNullable<PartialMetadata>[F]>[P]>
    >(family: F, property: P, subproperty: Q): NonNullable<NonNullable<NonNullable<PartialMetadata>[F]>[P]>[Q] {
        return get(this.metadata, family, property, subproperty);
    }

    public set<
        F extends keyof NonNullable<PartialMetadata>,
        P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>
    >(family: F, property: P, value: NonNullable<NonNullable<PartialMetadata>[F]>[P]): void {
        return set(this.metadata, value, family, property);
    }

    public setIfNotNull<
        F extends keyof NonNullable<PartialMetadata>,
        P extends keyof NonNullable<NonNullable<PartialMetadata>[F]>
    >(family: F, property: P, value: NonNullable<NonNullable<PartialMetadata>[F]>[P] | undefined): void {
        if (typeof value !== 'undefined' && value !== null) {
            return set(this.metadata, value, family, property);
        }
    }
    // #endregion

    // #region Classification specific
    public hasUnpublishedChanges(): boolean {
        return !!this.get(Family.RecordInformation, 'Classification')?.HasChanges;
    }

    public getPublishBatchId(): string | undefined {
        return this.get(Family.RecordInformation, 'Classification')?.BatchId;
    }
    // #endregion

    // #region Utils
    equals(item: any): boolean {
        return item instanceof PartialRecord && item.getFragmentId() === this.getFragmentId();
    }

    trackBy(): FragmentId {
        return this.getFragmentId() as FragmentId;
    }

    asJsonString(): string {
        // Strip _links from metadata before stringifying, otherwise API might complain this is not a recognized family
        return JSON.stringify(this.stripMetadata(this.metadata));
    }
    // #endregion
}

export function isPartialFragment(object: any): boolean {
    return object instanceof PartialRecord;
}
